import { getStoredToken } from "./auth";

const BASE = "/api";

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

/** Render any thrown value (ApiError, Error, unknown) as readable text.
 *  FastAPI 422s carry `detail` as an array of `{loc, msg, type}` objects, which
 *  naively stringify to "[object Object]" — flatten those to their messages. */
export function apiErrorMessage(e: unknown, fallback = "Request failed"): string {
  if (e instanceof ApiError) {
    const d = e.detail;
    if (typeof d === "string" && d.trim()) return d;
    if (Array.isArray(d)) {
      const msgs = d
        .map((x) =>
          x && typeof x === "object" && "msg" in x
            ? String((x as { msg: unknown }).msg)
            : String(x),
        )
        .filter(Boolean);
      if (msgs.length) return msgs.join("; ");
    }
    if (d && typeof d === "object" && "msg" in d) {
      return String((d as { msg: unknown }).msg);
    }
    return e.message || fallback;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };
  const token = getStoredToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let body: BodyInit | undefined;
  if (opts.body !== undefined && opts.body !== null) {
    if (
      typeof opts.body === "string" ||
      opts.body instanceof FormData ||
      opts.body instanceof URLSearchParams
    ) {
      body = opts.body as BodyInit;
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(opts.body);
    }
  }

  const resp = await fetch(`${BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body,
    signal: opts.signal,
  });

  if (resp.status === 401 && onUnauthorized) {
    onUnauthorized();
  }

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;

  if (!resp.ok) {
    const detail =
      typeof parsed === "object" && parsed !== null && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    throw new ApiError(resp.status, detail, `HTTP ${resp.status}`);
  }

  return parsed as T;
}
